import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
// y agrega FaEdit a los iconos de react-icons/fa
import {
  FaSave, FaTimes, FaImage, FaVideo, FaFile, FaUpload,
  FaTag, FaUser, FaDollarSign, FaSearchPlus, FaLock,
  FaGlobe, FaAlignLeft, FaBox, FaLayerGroup, FaEdit,
} from 'react-icons/fa';
import Select from 'react-select';
import { successAlert, errorAlert } from '../utils/Alerts';

const IMAGE_ACCEPT  = 'image/png, image/jpeg, image/jpg, image/gif, image/webp, image/svg+xml';
const IMAGE_FORMATS = 'PNG, JPG, GIF, WEBP, SVG';
const FILE_ACCEPT = '.pdf,.docx,.doc,.txt,.mp3,.mp4,.wav,.ogg,.xlsx,.xls,.pptx,.zip,.rar,.7z,.exe,.dmg,.pkg,.iso,.AppImage';
// const FILE_FORMATS = 'PDF, DOCX, TXT, MP3, MP4, ZIP, RAR, EXE, DMG, ISO';
const DESC_MAX      = 1000;

// Después de las constantes existentes, agrega:
const MEDIA_ACCEPT  = 'image/png, image/jpeg, image/jpg, image/gif, image/webp, image/svg+xml, video/mp4, audio/mpeg';
const MEDIA_FORMATS = 'PDF, PNG, JPG, GIF, WEBP, SVG, MP4, MP3';

const TABS = [
  { id: 'imagen', label: 'Imagen / Video', sublabel: null, icon: <FaImage />, dbType: 'imagen' },
  { id: 'producto', label: 'Producto Digital', sublabel: null,      icon: <FaBox />,    dbType: 'archivo' },
  { id: 'video',    label: 'Video',            sublabel: 'YouTube', icon: <FaVideo />,  dbType: 'video'   },
];

const fadeIn      = keyframes`from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}`;
const fadeInScale = keyframes`from{opacity:0;transform:scale(0.94)}to{opacity:1;transform:scale(1)}`;
const spin        = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const slideIn     = keyframes`from{opacity:0;transform:translateX(10px)}to{opacity:1;transform:translateX(0)}`;

const reactSelectStyles = (isDark) => ({
  control: (base, state) => ({
    ...base,
    background: isDark ? '#1a1a1a' : '#f7f8fa',
    borderColor: state.isFocused ? 'rgba(79,70,229,0.6)' : isDark ? '#2a2a2a' : '#e0e0e0',
    borderRadius: 9, boxShadow: 'none', minHeight: 40,
    '&:hover': { borderColor: 'rgba(79,70,229,0.5)' },
  }),
  menu: (base) => ({
    ...base, background: isDark ? '#1e1e1e' : '#fff',
    border: `1px solid ${isDark ? '#2a2a2a' : '#e0e0e0'}`,
    borderRadius: 9, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 50,
  }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected ? isDark ? 'rgba(129,140,248,0.2)' : 'rgba(79,70,229,0.1)'
      : state.isFocused ? isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' : 'transparent',
    color: state.isSelected ? (isDark ? '#818cf8' : '#4f46e5') : isDark ? '#e0e0e0' : '#222',
    fontWeight: state.isSelected ? 600 : 400, fontSize: 13, cursor: 'pointer', borderRadius: 6,
  }),
  multiValueLabel: (base) => ({ ...base, color: isDark ? '#818cf8' : '#4f46e5', fontSize: 12, fontWeight: 600 }),
  multiValueRemove: (base) => ({ ...base, color: isDark ? '#818cf8' : '#4f46e5', borderRadius: '0 6px 6px 0', '&:hover': { background: 'rgba(239,68,68,0.15)', color: '#f87171' } }),
  placeholder: (base) => ({ ...base, color: isDark ? '#3d3d3d' : '#c0c0c0', fontSize: 13 }),
  input: (base) => ({ ...base, color: isDark ? '#545454' : '#222', fontSize: 13 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, color: isDark ? '#555' : '#bbb', '&:hover': { color: isDark ? '#888' : '#888' } }),
  clearIndicator: (base) => ({ ...base, color: isDark ? '#555' : '#bbb', '&:hover': { color: '#f87171' } }),
  valueContainer: (base) => ({
    ...base,
    maxHeight: '100px', // Altura máxima antes de mostrar scroll
    overflowY: 'auto',   // Activa el scroll vertical
    padding: '2px 8px',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: isDark ? '#333' : '#ccc',
      borderRadius: '10px',
    },
  }),

  multiValue: (base) => ({ 
    ...base, 
    background: isDark ? 'rgba(129,140,248,0.15)' : 'rgba(79,70,229,0.1)', 
    borderRadius: 6,
    margin: '2px', // Un poco de margen para que no se peguen al hacer scroll
  }),
});

const getFileIcon = (name) => {
  const ext = name.split('.').pop().toLowerCase();
  const map = { pdf: '📄', doc: '📝', docx: '📝', txt: '📃', mp3: '🎵', wav: '🎵', ogg: '🎵', mp4: '🎬', xlsx: '📊', 
    xls: '📊', pptx: '📊', zip: '🗜️', exe: '💿', dmg: '💿', pkg: '📦', iso: '💿', rar: '🗜️', '7z': '🗜️', AppImage: '🐧'
   };
  return map[ext] || '📁';
};

function CreateContents({ token, theme }) {
  const navigate     = useNavigate();
  const { contentId } = useParams();        // ← nuevo
  const editId       = contentId || null;   // ← nuevo
  const isEdit       = !!editId;            // ← nuevo
  const isDark       = theme === 'dark';
  const fileInputRef  = useRef(null);
  const coverInputRef = useRef(null);
  const [priceDisplay, setPriceDisplay] = useState('');
  const [stock, setStock] = useState('');

  const [formData, setFormData] = useState({ title: '', themeIds: [], price: '', description: '' });
  const [selectedAssigned, setSelectedAssigned] = useState([]);
  const [themes, setThemes]                     = useState([]);
  const [assignedto, setAssignedTo]             = useState([]);
  const [processing, setProcessing]             = useState(false);
  const [activeTab, setActiveTab]               = useState('');

  const [errors, setErrors] = useState({});

  const galleryInputRef = useRef(null);
  const productGalleryInputRef = useRef(null);

  // Imagen
  // eslint-disable-next-line no-unused-vars
  const [image, setImage]               = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile]       = useState(null);
  const [gallery, setGallery]           = useState([]);
  const [dragOverImg, setDragOverImg]   = useState(false);
  const [lightbox, setLightbox]         = useState(false);

  // Producto Digital
  const [coverImage, setCoverImage]         = useState(null);
  const [coverPreview, setCoverPreview]     = useState(null);
  const [coverFile, setCoverFile]           = useState(null);
  const [productGallery, setProductGallery] = useState([]);
  const [productFiles, setProductFiles]     = useState([]);
  const [dragOverCover, setDragOverCover]   = useState(false);
  const [dragOverPdf, setDragOverPdf]       = useState(false);
  const [coverLightbox, setCoverLightbox]   = useState(false);

  // Video
  const [videoUrl, setVideoUrl] = useState('');

  const [visibility, setVisibility] = useState('publico');

  const [imageIsVideo, setImageIsVideo] = useState(false);

  const [coverIsVideo, setCoverIsVideo] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [themeRes, usersRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/user/list-themes`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/user/assignable-users`, { headers }),
        ]);
        setThemes(themeRes.data);
        setAssignedTo(usersRes.data);
      } catch (err) { console.error(err); }
    };
    fetchData();
  }, [token]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') { setLightbox(false); setCoverLightbox(false); } };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // ── Cargar datos en modo edición ──
  useEffect(() => {
    if (!isEdit || !token) return;
    const loadContent = async () => {
      try {
        const { data: c } = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/user/content-detail/${editId}`
        );

        const tabMap = { imagen: 'imagen', archivo: 'producto', video: 'video' };
        setActiveTab(tabMap[c.type] || '');

        setFormData({
          title:       c.title       || '',
          themeIds: Array.isArray(c.themeIds)
            ? c.themeIds.map(t => String(t._id || t))
            : c.themeId ? [String(c.themeId)] : [],
          price:       c.price != null ? String(c.price) : '',
          description: c.description || '',
        });

        setStock(c.stock != null ? String(c.stock) : '');

        if (c.price != null) {
          setPriceDisplay(formatPrice(c.price));
        }
        
        setVisibility(c.visibility || 'publico');
        setSelectedAssigned((c.assignedTo || []).map(a => String(a._id || a)));

       // --- NUEVA LÓGICA PARA LA GALERÍA ---
        if (c.gallery && c.gallery.length > 0) {
            // Si ya es URL de Cloudinary (http) la usa directo
            // Si es nombre de archivo local construye la URL del backend
            const galleryUrls = c.gallery.map(img =>
              img.startsWith('http') ? img : `${process.env.REACT_APP_API_URL}/${img}`
            );
            
            setGallery(galleryUrls);
            setProductGallery(galleryUrls); 
          }
        // ------------------------------------

        if (c.type === 'video') setVideoUrl(c.videoUrl || '');

        if (c.type === 'imagen') {
          const fileStr = Array.isArray(c.file) ? c.file[0] : c.file;
          const isVid = fileStr && fileStr.toLowerCase().endsWith('.mp4');
          const isAud = fileStr && fileStr.toLowerCase().endsWith('.mp3');

          if (isVid || isAud) {
            setImageIsVideo(true);
            setImagePreview('existing_media');
            setImage('existing_media');
          } else {
            try {
              const imgRes = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/user/img-content/${editId}`
              );
              if (imgRes.data.image) {
                setImage(imgRes.data.image);
                setImagePreview(imgRes.data.image);
              }
            } catch { /* sin preview */ }
          }
        }

        if (c.type === 'archivo') {
          const fileStr = Array.isArray(c.file) ? c.file[0] : c.file;
          if (fileStr) {
            const cleanName = fileStr.replace(/^\d+_/, '');
            setProductFiles([{ name: cleanName, size: 0, isExisting: true }]);
          }
          try {
            const imgRes = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/user/img-content/${editId}`
            );
            if (imgRes.data.image) {
              setCoverImage(imgRes.data.image);
              setCoverPreview(imgRes.data.image);
            }
          } catch { /* sin preview */ }
        }

      } catch (err) {
        console.error(err);
        errorAlert({ title: 'Error', text: 'No se pudo cargar el contenido' });
      }
    };
    loadContent();
  }, [editId, token]); // eslint-disable-line

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value, ...(name === 'theme' ? { themeId: value } : {}) }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (name === 'theme' && errors.themeId) setErrors(prev => ({ ...prev, themeId: '' }));
  };

  const handleDescChange = (e) => {
    if (e.target.value.length <= DESC_MAX) setFormData(prev => ({ ...prev, description: e.target.value }));
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (errors.tab) setErrors(prev => ({ ...prev, tab: '' }));
    setImageIsVideo(false);
    setCoverIsVideo(false);
    setImage(null); setImagePreview(null); setImageFile(null); setGallery([]);
    setCoverImage(null); setCoverPreview(null); setCoverFile(null); setProductGallery([]); setProductFiles([]);
    setVideoUrl('');
  };

  const handleImageChange = (f) => {
    if (!f) return;
    if (errors.imageFile) setErrors(prev => ({ ...prev, imageFile: '' }));
    const isVideo = f.type.startsWith('video/') || f.type.startsWith('audio/');
    setImageIsVideo(isVideo);
    if (isVideo) {
      const url = URL.createObjectURL(f);
      setImage(url);
      setImagePreview(url);
      setImageFile(f);
    } else {
      const reader = new FileReader();
      reader.onload = () => { setImage(reader.result); setImagePreview(reader.result); setImageFile(f); };
      reader.readAsDataURL(f);
    }
  };

  const handleCoverChange = (f) => {
    if (!f) return;
    if (errors.coverFile) setErrors(prev => ({ ...prev, coverFile: '' }));
      const isVideo = f.type.startsWith('video/') || f.type.startsWith('audio/');
      setCoverIsVideo(isVideo);
      if (isVideo) {
        const url = URL.createObjectURL(f);
        setCoverImage(url);
        setCoverPreview(url);
        setCoverFile(f);
      } else {
        const reader = new FileReader();
        reader.onload = () => { setCoverImage(reader.result); setCoverPreview(reader.result); setCoverFile(f); };
        reader.readAsDataURL(f);
      }
    };

  const handleProductFileAdd = (newFiles) => {
    setProductFiles(prev => [...prev, ...Array.from(newFiles)].slice(0, 1));
  };

  const truncateName = (name) => name.length > 30 ? name.substring(0, 27) + '...' : name;

  const handleSubmit = async (e) => {
      e.preventDefault();

          // Objeto para acumular errores
      let tempErrors = {};
      if (!formData.title) tempErrors.title = "El título es requerido";
      if (!formData.themeIds.length) tempErrors.themeId = "Debes seleccionar al menos un tema";
      if (!activeTab) tempErrors.tab = "Selecciona un tipo de contenido";
      if (!isEdit) {
        if (activeTab === 'imagen' && !imageFile) tempErrors.imageFile = "Debes subir una imagen o video";
        if (activeTab === 'producto' && !coverFile) tempErrors.coverFile = "Debes subir una portada";
        if (activeTab === 'video' && !videoUrl) tempErrors.videoUrl = "Debes ingresar una URL de YouTube";
      }
      
      if (Object.keys(tempErrors).length > 0) {
        setErrors(tempErrors);
        return;
      }

      setErrors({}); // Limpiar errores si todo está bien
      setProcessing(true);
      try {
      const tab = TABS.find(t => t.id === activeTab);
      const assignedToPayload = assignedto
        .filter(c => selectedAssigned.includes(String(c._id)))
        .map(c => ({ _id: c._id, username: c.username }));
      const fd = new FormData();
      fd.append('title', formData.title);
      fd.append('description', formData.description);
      fd.append('type', tab.dbType);
      fd.append('themeIds', JSON.stringify(formData.themeIds));
      fd.append('assignedTo', JSON.stringify(assignedToPayload));
      fd.append('price', formData.price || 0);
      fd.append('visibility', visibility);
      fd.append('stock', stock === '' ? -1 : Number(stock)); 
        // -1 = sin límite de stock (no aplica)

      if (activeTab === 'imagen') {
      if (imageFile) fd.append('file', imageFile);
        gallery.forEach(f => {
          if (f instanceof File || f instanceof Blob) {
            fd.append('gallery', f);
          } else {
            fd.append('existingGallery', f);
          }
        });
      } else if (activeTab === 'producto') {
        if (coverFile) {
          fd.append('coverVideo', coverFile);
        } else if (coverImage && coverImage !== 'existing_media' && coverImage.startsWith('http')) {
          fd.append('image', coverImage);
        }
      // Solo enviar archivos nuevos (no los existentes)
      productFiles
        .filter(f => !f.isExisting)
        .forEach(f => fd.append('file', f));
        productGallery.forEach(f => {
          if (f instanceof File || f instanceof Blob) {
            fd.append('gallery', f);
          } else {
            fd.append('existingGallery', f);
          }
        });
      } else if (activeTab === 'video') {
        fd.append('videoUrl', videoUrl);
      }

      if (isEdit) {      
        await axios.put(`${process.env.REACT_APP_API_URL}/api/user/content/${editId}`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        successAlert({ text: '¡Contenido actualizado correctamente!', timer: 2000 });
        navigate(-1);
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/user/contents`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        successAlert({ text: '¡Contenido creado correctamente!', timer: 2000 });
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.error(err);
      errorAlert({ title: 'Error', text: err?.response?.data?.message || err?.response?.data?.details || 'Error al procesar la solicitud' });
    } finally { setProcessing(false); }
  };

  const formatPrice = (val) => {
  const num = parseFloat(String(val).replace(/\./g, '').replace(',', '.'));
  if (isNaN(num)) return '';
  return num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

  const handlePriceChange = (e) => {
    // 1. Obtener solo los números del input
    let value = e.target.value.replace(/\D/g, '');
    
    // 2. Si está vacío, resetear estados
    if (!value) {
      setPriceDisplay('');
      setFormData(prev => ({ ...prev, price: '' }));
      return;
    }

    // 3. Convertir a un número con dos decimales (ej: 200000 -> 2000.00)
    const numberValue = (parseFloat(value) / 100).toFixed(2);
    
    // 4. Formatear para mostrar al usuario (puntos para miles, coma para decimal)
    const formatted = new Intl.NumberFormat('es-VE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numberValue);

    // 5. Actualizar la vista y el valor real para la base de datos
    setPriceDisplay(formatted);
    setFormData(prev => ({ ...prev, price: numberValue }));
  };

  const handlePriceBlur = () => {
    // Si el campo no está vacío, nos aseguramos que el formData tenga el número puro
    if (priceDisplay) {
      const numeric = priceDisplay.replace(/\./g, '').replace(',', '.');
      setFormData(prev => ({ ...prev, price: numeric }));
    }
  };

  return (
    <PageWrap active={isDark}>
      <WideCard active={isDark}>

        {/* ════════════════════════════════
            HEADER — Título + Tabs de tipo
        ════════════════════════════════ */}
        <TopBar active={isDark}>
          <TopBarLeft>
            <HeaderIcon active={isDark}>{isEdit ? <FaEdit /> : <FaUpload />}</HeaderIcon>
            <HeaderTitle active={isDark}>{isEdit ? 'Editar Contenido' : 'Crear Contenido'}</HeaderTitle>
          </TopBarLeft>

          <HeaderSub active={isDark}>{isEdit ? 'Modifica los datos del contenido' : 'Completa y elige el tipo de información'}</HeaderSub>

          <TabRow style={{ justifyContent: 'flex-end' }}>
            {TABS.map(tab => (
              <TabChip key={tab.id} type="button" active={isDark}
                selected={activeTab === tab.id}
                onClick={() => handleTabChange(tab.id)} disabled={processing || isEdit}>
                <TabChipIcon selected={activeTab === tab.id}>{tab.icon}</TabChipIcon>
                <TabChipText>
                  <TabChipLabel selected={activeTab === tab.id} active={isDark}>{tab.label}</TabChipLabel>
                  {tab.sublabel && <TabChipSub selected={activeTab === tab.id}>{tab.sublabel}</TabChipSub>}
                </TabChipText>
              </TabChip>
            ))}
          </TabRow>
        </TopBar>

        {/* ════════════════════════════════
            BODY — Dos columnas
        ════════════════════════════════ */}
        <form onSubmit={handleSubmit}>
          <TwoColLayout>

            {/* ── COLUMNA IZQUIERDA ── */}
            <LeftCol active={isDark}>

              <FieldGroup>
                <FieldLabel active={isDark}>Título <Red>*</Red></FieldLabel>
                <StyledInput active={isDark} name="title" value={formData.title}
                  onChange={handleChange} 
                  placeholder="Título del contenido" 
                  // required 
                  disabled={processing}
                  style={{
                    borderColor: errors.title ? '#ef4444' : '',
                  }}
                  />
                  {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
              </FieldGroup>

              <FieldGroup>
                <FieldLabel active={isDark}><FaAlignLeft /> Descripción</FieldLabel>
                <TextareaWrapper>
                  <StyledTextarea active={isDark} name="description" value={formData.description}
                    onChange={handleDescChange} placeholder="Describe brevemente el contenido..."
                    disabled={processing} rows={4} />
                  <CharCount active={isDark} nearLimit={formData.description.length >= DESC_MAX * 0.9}>
                    {formData.description.length}/{DESC_MAX}
                  </CharCount>
                </TextareaWrapper>
              </FieldGroup>

              <Divider active={isDark} />

{/* Fila 1: Visibilidad + Tema */}
<TwoColSmall>
  <FieldGroup>
    <FieldLabel active={isDark}>Visibilidad</FieldLabel>
    <VisibilityRow>
      <VisChip type="button" active={isDark} selected={visibility === 'publico'} isPublic
        onClick={() => setVisibility('publico')} disabled={processing}>
        <FaGlobe /> Público
      </VisChip>
      <VisChip type="button" active={isDark} selected={visibility === 'privado'}
        onClick={() => setVisibility('privado')} disabled={processing}>
        <FaLock /> Privado
      </VisChip>
    </VisibilityRow>
  </FieldGroup>

  <FieldGroup>
    <FieldLabel active={isDark}><FaTag /> Tema <Red>*</Red></FieldLabel>
    <div style={{ borderRadius: 9, border: errors.themeId ? '1px solid #ef4444' : 'none' }}>
      <Select
        isMulti
        isDisabled={processing}
        options={themes.map(t => ({ value: t._id, label: t.name }))}
        value={themes
          .filter(t => formData.themeIds.includes(String(t._id)))
          .map(t => ({ value: t._id, label: t.name }))}
        onChange={opts => {
          setFormData(prev => ({ ...prev, themeIds: (opts || []).map(o => String(o.value)) }));
          if (errors.themeId) setErrors(prev => ({ ...prev, themeId: '' }));
        }}
        placeholder="Selecciona temas..."
        noOptionsMessage={() => 'Sin resultados'}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          ...reactSelectStyles(isDark),
          menuPortal: base => ({ ...base, zIndex: 9999 }),
          control: (base, state) => ({
            ...reactSelectStyles(isDark).control(base, state),
            borderColor: errors.themeId
              ? '#ef4444'
              : state.isFocused ? 'rgba(79,70,229,0.6)' : isDark ? '#2a2a2a' : '#e0e0e0',
          }),
        }}
      />
    </div>
    {errors.themeId && <ErrorMessage>{errors.themeId}</ErrorMessage>}
  </FieldGroup>
</TwoColSmall>

{/* Fila 2: Precio + Stock */}
<TwoColSmall>
  <FieldGroup>
    <FieldLabel active={isDark}><FaDollarSign /> Precio</FieldLabel>
    <StyledInput
      active={isDark}
      name="price"
      type="text"
      inputMode="numeric"
      value={priceDisplay}
      onChange={handlePriceChange}
      onBlur={handlePriceBlur}
      placeholder="0,00"
      disabled={processing}
    />
  </FieldGroup>

  <FieldGroup>
    <FieldLabel active={isDark}><FaBox /> Stock</FieldLabel>
    <StyledInput
      active={isDark}
      name="stock"
      type="number"
      min="0"
      value={stock}
      onChange={e => setStock(e.target.value)}
      placeholder="Vacío = ilimitado"
      disabled={processing}
    />
    <StockHint active={isDark}>
      {stock === ''
        ? '∞ Sin límite de stock'
        : Number(stock) === 0
          ? '🟠 Se marcará como Vendido'
          : `🟢 ${stock} unidad${Number(stock) !== 1 ? 'es' : ''} disponible${Number(stock) !== 1 ? 's' : ''}`
      }
    </StockHint>
  </FieldGroup>
</TwoColSmall>

{/* Fila 3: Asignado a (ancho completo) */}
<FieldGroup>
  <FieldLabel active={isDark}><FaUser /> Asignado a</FieldLabel>
  <Select isMulti isDisabled={processing}
    options={assignedto.map(c => ({ value: c._id, label: c.name ? `${c.name} (${c.username})` : c.username }))}
    value={assignedto.filter(c => selectedAssigned.includes(c._id)).map(c => ({ value: c._id, label: c.name ? `${c.name} (${c.username})` : c.username }))}
    onChange={opts => setSelectedAssigned((opts || []).map(o => o.value))}
    placeholder="Usuarios..." noOptionsMessage={() => 'Sin resultados'}
    menuPortalTarget={document.body}
    menuPosition="fixed"
    styles={{
      ...reactSelectStyles(isDark),
      menuPortal: base => ({ ...base, zIndex: 9999 }),
    }} />
  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
    <button
      type="button"
      disabled={processing}
      onClick={() => setSelectedAssigned(assignedto.map(c => String(c._id)))}
      style={{
        padding: '4px 10px', borderRadius: '6px', fontSize: '10.5px', fontWeight: 600,
        cursor: 'pointer', transition: 'all 0.18s',
        background: 'rgba(79,70,229,0.1)', border: '1px solid rgba(79,70,229,0.25)',
        color: isDark ? '#818cf8' : '#4f46e5', display: 'inline-flex', alignItems: 'center'
      }}
    >✓ Todos</button>
    <button
      type="button"
      disabled={processing}
      onClick={() => setSelectedAssigned([])}
      style={{
        padding: '4px 10px', borderRadius: '6px', fontSize: '10.5px', fontWeight: 600,
        cursor: 'pointer', transition: 'all 0.18s',
        background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)',
        color: '#f87171', display: 'inline-flex', alignItems: 'center'
      }}
    >✕ Limpiar</button>
  </div>
</FieldGroup>

            </LeftCol>
           
           <TabRowMobile active={isDark}>
              {TABS.map(tab => (
                <TabChip key={tab.id} type="button" active={isDark}
                  selected={activeTab === tab.id}
                  onClick={() => handleTabChange(tab.id)} disabled={processing}>
                  <TabChipIcon selected={activeTab === tab.id}>{tab.icon}</TabChipIcon>
                  <TabChipText>
                    <TabChipLabel selected={activeTab === tab.id} active={isDark}>{tab.label}</TabChipLabel>
                    {tab.sublabel && <TabChipSub selected={activeTab === tab.id}>{tab.sublabel}</TabChipSub>}
                  </TabChipText>
                </TabChip>
              ))}
            </TabRowMobile>

            {/* ── COLUMNA DERECHA ── */}
            <RightCol active={isDark}>

            {!activeTab && (
              <EmptyRight active={isDark}>
                <EmptyRow>
                  <EmptyTexts>
                    <EmptyIcon active={isDark}><FaLayerGroup /></EmptyIcon>
                    <EmptyTitle active={isDark}>Selecciona un tipo de contenido <Red>*</Red></EmptyTitle>
                    <EmptyText active={isDark}>Elige arriba para ver las opciones de carga</EmptyText>
                    {errors.tab && <ErrorMessage style={{ marginTop: 6 }}>{errors.tab}</ErrorMessage>}
                  </EmptyTexts>
                  <ArrowIndicator>
                    <ArrowSvg viewBox="0 0 24 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M12 56 L12 4 M12 4 L5 14 M12 4 L19 14"
                        stroke="url(#arrowGrad)"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <defs>
                        <linearGradient id="arrowGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#4f46e5" />
                          <stop offset="100%" stopColor="#7c3aed" />
                        </linearGradient>
                      </defs>
                    </ArrowSvg>
                  </ArrowIndicator>
                  </EmptyRow>
                </EmptyRight>
              )}

              {/* ── TAB IMAGEN ── */}
              {activeTab === 'imagen' && (
                <RightContent key="imagen">
                  <RightSectionTitle active={isDark}><FaImage /> Imagen principal <Red>*</Red></RightSectionTitle>
                  {imagePreview ? (
                    <PreviewWrapper>
                      {imagePreview === 'existing_media' ? (
                        <div style={{
                          padding: '30px 20px', textAlign: 'center',
                          background: '#111', borderRadius: 10,
                          border: '1px solid #222', color: '#afafaf',
                          fontSize: 13, display: 'flex',
                          flexDirection: 'column', alignItems: 'center', gap: 8
                        }}>
                          <span style={{ fontSize: 32 }}>🎬</span>
                          <span>Video/Audio existente guardado</span>
                          <span style={{ fontSize: 11, color: '#7f7f7f' }}>
                            Sube un nuevo archivo para reemplazarlo
                          </span>
                        </div>
                      ) : imageIsVideo ? (
                        <video
                          src={imagePreview}
                          controls
                          style={{ width: '100%', maxHeight: '230px', borderRadius: '10px', display: 'block', background: '#000' }}
                        />
                      ) : (
                        <PreviewImg src={imagePreview} alt="preview" />
                      )}
                      <PreviewActions>
                        {imagePreview !== 'existing_media' && !imageIsVideo && (
                          <PreviewBtn type="button" onClick={() => setLightbox(true)}>
                            <FaSearchPlus /> Ver
                          </PreviewBtn>
                        )}
                        <PreviewBtn type="button" variant="change" onClick={() => fileInputRef.current?.click()}>
                          <FaUpload /> Cambiar
                        </PreviewBtn>
                      </PreviewActions>
                      <HiddenInputOff ref={fileInputRef} type="file" accept={MEDIA_ACCEPT}
                        onChange={e => handleImageChange(e.target.files[0])} disabled={processing} />
                    </PreviewWrapper>
                  ) : (
                    <DropZone active={isDark} dragOver={dragOverImg}
                      onDragOver={e => { e.preventDefault(); setDragOverImg(true); }}
                      onDragLeave={() => setDragOverImg(false)}
                      onDrop={e => { e.preventDefault(); setDragOverImg(false); const f = e.dataTransfer.files[0]; if (f) handleImageChange(f); }}>
                      <DropContent>
                        <DropIcon active={isDark}><FaImage /></DropIcon>
                        <DropText active={isDark}>Arrastra o haz clic para seleccionar</DropText>
                        <FormatBadgeRow>{MEDIA_FORMATS.split(', ').map(f => <FormatBadge key={f} active={isDark}>{f}</FormatBadge>)}</FormatBadgeRow>
                      </DropContent>
                      <HiddenInput type="file" accept={MEDIA_ACCEPT} onChange={e => handleImageChange(e.target.files[0])} disabled={processing} />

                    </DropZone>
                  )}
                  {errors.imageFile && <ErrorMessage>{errors.imageFile}</ErrorMessage>}

                  {imagePreview && (
                    <>
                      <RightSectionTitle active={isDark} style={{ marginTop: 18 }}>
                        <FaImage /> Galería extra <Muted>(hasta 3)</Muted>
                      </RightSectionTitle>
                     <GalleryRow>
                      {gallery.map((f, i) => {
                        // PROTECCIÓN TOTAL: Solo ejecutamos createObjectURL si NO es un string
                        const isBinary = f instanceof File || f instanceof Blob;
                        const imgSrc = isBinary ? URL.createObjectURL(f) : f;
                        
                        return (
                          <GalleryThumb key={i}>
                            <img src={imgSrc} alt={`g-${i}`} />
                            <RemoveThumb 
                              type="button" 
                              onClick={() => setGallery(p => p.filter((_, j) => j !== i))}
                            >
                              ✕
                            </RemoveThumb>
                          </GalleryThumb>
                        );
                      })}
                      {gallery.length < 3 && (
                        <GalleryAdd active={isDark} onClick={() => galleryInputRef.current?.click()}>
                          <FaImage /><span>Agregar</span>
                          <input
                            ref={galleryInputRef}
                            type="file"
                            accept={IMAGE_ACCEPT}
                            multiple
                            style={{ display: 'none' }}
                            onChange={e => {
                              const newFiles = Array.from(e.target.files);
                              setGallery(prev => [...prev, ...newFiles].slice(0, 3));
                              e.target.value = '';
                            }}
                          />
                        </GalleryAdd>
                      )}
                    </GalleryRow>
                    </>
                  )}
                </RightContent>
              )}

              {/* ── TAB PRODUCTO DIGITAL ── */}
              {activeTab === 'producto' && (
                <RightContent key="producto">
                  <RightSectionTitle active={isDark}><FaImage /> Portada del producto <Red>*</Red></RightSectionTitle>
                  {coverPreview ? (
                    <PreviewWrapper>
                      {coverIsVideo ? (
                        <video
                          src={coverPreview}
                          controls
                          style={{ width: '100%', maxHeight: '230px', borderRadius: '10px', display: 'block', background: '#000' }}
                        />
                      ) : (
                        <PreviewImg src={coverPreview} alt="cover" />
                      )}
                      <PreviewActions>
                        {!coverIsVideo && (
                          <PreviewBtn type="button" onClick={() => setCoverLightbox(true)}><FaSearchPlus /> Ver</PreviewBtn>
                        )}
                        <PreviewBtn type="button" variant="change" onClick={() => coverInputRef.current?.click()}><FaUpload /> Cambiar</PreviewBtn>
                      </PreviewActions>
                      <HiddenInputOff ref={coverInputRef} type="file" accept={MEDIA_ACCEPT}
                        onChange={e => handleCoverChange(e.target.files[0])} disabled={processing} />
                    </PreviewWrapper>
                  ) : (
                    <DropZone active={isDark} dragOver={dragOverCover}
                      onDragOver={e => { e.preventDefault(); setDragOverCover(true); }}
                      onDragLeave={() => setDragOverCover(false)}
                      onDrop={e => { e.preventDefault(); setDragOverCover(false); const f = e.dataTransfer.files[0]; if (f) handleCoverChange(f); }}>
                      <DropContent>
                        <DropIcon active={isDark}><FaImage /></DropIcon>
                        <DropText active={isDark}>Portada (lo que verá el comprador)</DropText>
                        <FormatBadgeRow>{IMAGE_FORMATS.split(', ').map(f => <FormatBadge key={f} active={isDark}>{f}</FormatBadge>)}</FormatBadgeRow>
                      </DropContent>
                      <HiddenInput type="file" accept={MEDIA_ACCEPT} onChange={e => handleCoverChange(e.target.files[0])} disabled={processing} />
                    </DropZone>
                  )}
                  {errors.coverFile && <ErrorMessage>{errors.coverFile}</ErrorMessage>}

                  {coverPreview && (
                    <>
                      <RightSectionTitle active={isDark} style={{ marginTop: 16 }}>
                        <FaImage /> Imágenes de muestra <Muted>(hasta 3)</Muted>
                      </RightSectionTitle>
                     <GalleryRow>
                      {productGallery.map((f, i) => {
                        // Aplicamos la misma lógica de protección aquí
                        const isBinary = f instanceof File || f instanceof Blob;
                        const imgSrc = isBinary ? URL.createObjectURL(f) : f;
                        
                        return (
                          <GalleryThumb key={i}>
                            <img src={imgSrc} alt={`g-${i}`} />
                            <RemoveThumb 
                              type="button" 
                              onClick={() => setProductGallery(p => p.filter((_, j) => j !== i))}
                            >
                              ✕
                            </RemoveThumb>
                          </GalleryThumb>
                        );
                      })}
                      {productGallery.length < 3 && (
                        <GalleryAdd active={isDark} onClick={() => productGalleryInputRef.current?.click()}>
                          <FaImage /><span>Agregar</span>
                          <input
                            ref={productGalleryInputRef}
                            type="file"
                            accept={IMAGE_ACCEPT}
                            multiple
                            style={{ display: 'none' }}
                            onChange={e => {
                              const newFiles = Array.from(e.target.files);
                              setGallery(prev => [...prev, ...newFiles].slice(0, 3));
                              e.target.value = '';
                            }}
                          />
                        </GalleryAdd>
                      )}
                    </GalleryRow>
                    </>
                  )}

                  <RightSectionTitle active={isDark} style={{ marginTop: 20 }}>
                    <FaFile /> Archivos del producto <Red>*</Red>
                    <CountBadge active={isDark}>{productFiles.length}/1</CountBadge>
                  </RightSectionTitle>

                  <ProductFileHint active={isDark}>
                    El comprador recibirá estos archivos al adquirir el producto.
                  </ProductFileHint>

                  {productFiles.length > 0 && (
                    <FileList>
                      {productFiles.map((f, i) => (
                        <FileItem key={i} active={isDark}>
                          <FileIconSpan>{f.isExisting ? '📦' : getFileIcon(f.name)}</FileIconSpan>
                          <FileName active={isDark}>
                            {truncateName(f.name)}
                            {f.isExisting && (
                              <span style={{ fontSize: 10, color: '#555', marginLeft: 6, fontStyle: 'italic' }}>
                                (existente)
                              </span>
                            )}
                          </FileName>
                          <FileSize active={isDark}>
                            {f.isExisting ? 'guardado' : `${(f.size / 1024).toFixed(0)} KB`}
                          </FileSize>
                          {isEdit ? (
                            <>
                              <PreviewBtn
                                type="button"
                                variant="change"
                                style={{ padding: '4px 10px', fontSize: 10 }}
                                onClick={() => document.getElementById('replace-file-input').click()}
                              >
                                <FaUpload /> Cambiar
                              </PreviewBtn>
                              <input
                                id="replace-file-input"
                                type="file"
                                accept={FILE_ACCEPT}
                                style={{ display: 'none' }}
                                onChange={e => {
                                  const newFile = e.target.files[0];
                                  if (!newFile) return;
                                  setProductFiles([newFile]);
                                }}
                              />
                            </>
                          ) : (
                            <RemoveFileBtn type="button" onClick={() => setProductFiles(p => p.filter((_, j) => j !== i))}>
                              <FaTimes />
                            </RemoveFileBtn>
                          )}
                        </FileItem>
                      ))}
                    </FileList>
                  )}

                  {productFiles.length < 1 && (
                    <DropZone active={isDark} dragOver={dragOverPdf} compact
                      onDragOver={e => { e.preventDefault(); setDragOverPdf(true); }}
                      onDragLeave={() => setDragOverPdf(false)}
                      onDrop={e => { e.preventDefault(); setDragOverPdf(false); handleProductFileAdd(e.dataTransfer.files); }}>
                      <DropContent>
                        <DropIcon active={isDark} style={{ fontSize: 22 }}><FaFile /></DropIcon>
                        <DropText active={isDark}>Arrastra archivos o haz clic</DropText>
                        <FormatBadgeRow>
                          {MEDIA_FORMATS.split(', ').map(f => <FormatBadge key={f} active={isDark}>{f}</FormatBadge>)}
                        </FormatBadgeRow>
                      </DropContent>
                      <HiddenInput type="file" accept={FILE_ACCEPT} multiple
                        onChange={e => handleProductFileAdd(e.target.files)} disabled={processing} />
                    </DropZone>
                  )}
                </RightContent>
              )}

              {/* ── TAB VIDEO ── */}
              {activeTab === 'video' && (
                <RightContent key="video">
                  <RightSectionTitle active={isDark}><FaVideo /> URL de YouTube <Red>*</Red></RightSectionTitle>
                  <StyledInput 
                  active={isDark} 
                  type="url" 
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={videoUrl} 
                  onChange={e => setVideoUrl(e.target.value)} 
                  // required 
                  disabled={processing} />
                  {errors.videoUrl && <ErrorMessage>{errors.videoUrl}</ErrorMessage>}
                  {videoUrl && videoUrl.includes('youtube.com/watch?v=') ? (
                    <VideoPreview active={isDark}>
                      <iframe src={`https://www.youtube.com/embed/${new URL(videoUrl).searchParams.get('v')}`}
                        title="preview" allowFullScreen
                        style={{ border: 'none', borderRadius: 10, width: '100%', height: 280 }} />
                    </VideoPreview>
                  ) : (
                    <VideoPlaceholder active={isDark}>
                      <FaVideo style={{ fontSize: 32, opacity: 0.15 }} />
                      <span>El preview aparecerá aquí</span>
                    </VideoPlaceholder>
                  )}
                </RightContent>
              )}

            </RightCol>
          </TwoColLayout>

          {/* ── FOOTER ── */}
          <FormFooter active={isDark}>
            <CancelBtn active={isDark} type="button" onClick={() => navigate(-1)}  disabled={processing}>
              <FaTimes /> Cancelar
            </CancelBtn>
            <SaveBtn type="submit" disabled={processing}>
              {processing ? <><Spinner /> Guardando...</> : <><FaSave /> {isEdit ? 'Guardar Cambios' : 'Guardar Contenido'}</>}
            </SaveBtn>
          </FormFooter>
        </form>

      </WideCard>

      {lightbox && (
        <LightboxOverlay onClick={() => setLightbox(false)}>
          <LightboxImg src={imagePreview} alt="full" onClick={e => e.stopPropagation()} />
          <LightboxClose type="button" onClick={() => setLightbox(false)}><FaTimes /></LightboxClose>
        </LightboxOverlay>
      )}
      {coverLightbox && (
        <LightboxOverlay onClick={() => setCoverLightbox(false)}>
          <LightboxImg src={coverPreview} alt="cover full" onClick={e => e.stopPropagation()} />
          <LightboxClose type="button" onClick={() => setCoverLightbox(false)}><FaTimes /></LightboxClose>
        </LightboxOverlay>
      )}
    </PageWrap>
  );
}

/* ═══════════════════════════════════════════
   STYLED COMPONENTS
═══════════════════════════════════════════ */

const PageWrap = styled.div`
  background-color: ${p => p.active ? '#000000' : '#ffffff'};
  min-height: 100%;
  padding: 28px 24px;
  display: flex; justify-content: center; align-items: flex-start;
  transition: background-color 0.3s;
  @media(max-width: 768px) { padding: 14px 10px; }
`;

const WideCard = styled.div`
  background: ${p => p.active ? '#111' : '#fff'};
  border: 1px solid ${p => p.active ? '#1e1e1e' : '#e4e4e7'};
  border-radius: 18px; width: 100%; max-width: 1140px;
  box-shadow: 0 8px 40px ${p => p.active ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.07)'};
  animation: ${fadeIn} 0.3s ease; overflow: visible;
`;

/* ── Top bar: título + tabs ── */
const TopBar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 14px;
  padding: 18px 28px;
  border-bottom: 1px solid ${p => p.active ? '#1a1a1a' : '#f0f0f0'};
  background: ${p => p.active ? 'rgba(255,255,255,0.018)' : 'rgba(0,0,0,0.01)'};
  border-radius: 18px 18px 0 0;
  @media(max-width: 700px) {
    grid-template-columns: 1fr;
    padding: 14px 16px;
    gap: 6px;
    border-bottom: none;
  }
`;

const TopBarLeft = styled.div`display: flex; align-items: center; gap: 13px; flex-shrink: 0; justify-self: start;`;

const HeaderIcon = styled.div`
  width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
  background: ${p => p.active ? 'rgba(129,140,248,0.1)' : 'rgba(79,70,229,0.08)'};
  border: 1px solid ${p => p.active ? 'rgba(129,140,248,0.2)' : 'rgba(79,70,229,0.14)'};
  color: ${p => p.active ? '#818cf8' : '#4f46e5'};
  display: flex; align-items: center; justify-content: center; font-size: 15px;
`;

const HeaderTitle = styled.h2`
  margin: 0; font-size: 1rem; font-weight: 700;
  color: ${p => p.active ? '#fff' : '#111'}; line-height: 1.2;
`;

const HeaderSub = styled.p`
  margin: 0; font-size: 11px; font-weight: 500; letter-spacing: 0.3px;
  color: ${p => p.active ? '#bebdbd' : '#b0b0b0'};
  text-align: center; justify-self: center;
  transform: translateX(-30px);
  @media(max-width: 700px) {
    display: none;
  }
`;

/* Tabs en el header */
const TabRow = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap;
  @media(max-width: 700px) {
    display: none;
  }
`;

const TabRowMobile = styled.div`
  display: none;
  @media(max-width: 700px) {
    display: flex; gap: 8px; flex-wrap: wrap;
    width: 95%; padding: 12px 16px;
    border-top: 1px solid ${p => p.active ? '#1a1a1a' : '#f0f0f0'};
    border-bottom: 1px solid ${p => p.active ? '#1a1a1a' : '#f0f0f0'};
  }
`;

const TabChip = styled.button`
  display: flex; align-items: center; gap: 9px;
  padding: 8px 16px; border-radius: 10px; cursor: pointer;
  transition: all 0.18s; outline: none; text-align: left;
  border: 1px solid ${p => p.selected
    ? 'rgba(79,70,229,0.55)'
    : p.active ? '#242424' : '#e4e4e7'};
  background: ${p => p.selected
    ? (p.active ? 'rgba(79,70,229,0.14)' : 'rgba(79,70,229,0.07)')
    : 'transparent'};
  &:hover:not(:disabled) {
    border-color: rgba(79,70,229,0.45);
    background: ${p => p.active ? 'rgba(79,70,229,0.08)' : 'rgba(79,70,229,0.04)'};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  @media(max-width: 700px) {
    padding: 7px 12px;
    flex: 1;
    justify-content: center;
  }
`;

const TabChipIcon = styled.div`
  font-size: 14px;
  color: ${p => p.selected ? '#818cf8' : '#777'};
  flex-shrink: 0;
`;

const TabChipText  = styled.div`display: flex; flex-direction: column; gap: 1px;`;
const TabChipLabel = styled.span`font-size: 12px; font-weight: 700; color: ${p => p.selected ? '#818cf8' : p.active ? '#ccc' : '#444'};`;
const TabChipSub = styled.span`font-size: 10px; font-weight: 500; color: ${p => p.selected ? 'rgba(129,140,248,0.65)' : '#aaa'};`;
/* ── Two col body ── */
const TwoColLayout = styled.div`
  display: grid;
  grid-template-columns: 460px 1fr;
  min-height: 480px;
  @media(max-width: 900px) { 
    grid-template-columns: 1fr;
    min-height: unset;
  }
`;

const LeftCol = styled.div`
  padding: 24px 28px;
  border-right: 1px solid ${p => p.active ? '#1a1a1a' : '#f0f0f0'};
  display: flex; flex-direction: column; gap: 16px;
  @media(max-width: 900px) {
    border-right: none;
    border-bottom: none;
    padding: 16px;
  }
`;

const RightCol = styled.div`
  padding: 24px 28px;
  display: flex; flex-direction: column;
  background: ${p => p.active ? 'rgba(255,255,255,0.008)' : 'rgba(0,0,0,0.008)'};
  @media(max-width: 900px) {
    padding: 16px;
  }
`;

const RightContent = styled.div`
  display: flex; flex-direction: column; gap: 12px;
  animation: ${slideIn} 0.22s ease;
`;

const RightSectionTitle = styled.div`
  display: flex; align-items: center; gap: 7px;
  font-size: 10.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.7px; color: ${p => p.active ? '#898989' : '#999'};
  svg { font-size: 10px; }
`;

const EmptyRight = styled.div`
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 10px; text-align: center; padding: 40px 20px;
  @media(max-width: 700px) {
    padding: 20px 16px;
  }
`;

const EmptyIcon  = styled.div`font-size: 38px; color: ${p => p.active ? '#777777' : '#e4e4e7'};`;
const EmptyTitle = styled.p`margin: 0; font-size: 13px; font-weight: 700; color: ${p => p.active ? '#777777' : '#ccc'};`;
const EmptyText  = styled.p`margin: 0; font-size: 11px; color: ${p => p.active ? '#777777' : '#d0d0d0'}; max-width: 200px; line-height: 1.5;`;

const Divider = styled.div`height: 1px; background: ${p => p.active ? '#1a1a1a' : '#f0f0f0'};`;

const Muted = styled.span`font-weight: 400; text-transform: none; letter-spacing: 0; color: #555; font-size: 10px; margin-left: 4px;`;

/* Form fields */
const TwoColSmall = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  @media(max-width: 600px) { grid-template-columns: 1fr; }
`;

const FieldGroup = styled.div`display: flex; flex-direction: column; gap: 6px;`;

const FieldLabel = styled.label`
  display: flex; align-items: center; gap: 6px;
  font-size: 10.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.7px; color: ${p => p.active ? '#484848' : '#9a9a9a'};
  svg { font-size: 10px; }
`;

const Red = styled.span`color: #ef4444; margin-left: 1px;`;

const StyledInput = styled.input`
  background: ${p => p.active ? '#1a1a1a' : '#f7f8fa'};
  border: 1px solid ${p => p.active ? '#2a2a2a' : '#e4e4e7'};
  border-radius: 9px; padding: 9px 13px; font-size: 13.5px;
  color: ${p => p.active ? '#e0e0e0' : '#222'};
  outline: none; width: 100%; box-sizing: border-box; transition: border-color 0.2s;
  &:focus { border-color: rgba(79,70,229,0.55); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &::placeholder { color: ${p => p.active ? '#333' : '#c5c5c5'}; }
`;

// const StyledSelect = styled.select`
//   background: ${p => p.active ? '#1a1a1a' : '#f7f8fa'};
//   border: 1px solid ${p => p.active ? '#2a2a2a' : '#e4e4e7'};
//   border-radius: 9px; padding: 9px 13px; font-size: 13.5px;
//   color: ${p => p.active ? '#e0e0e0' : '#222'};
//   outline: none; width: 100%; cursor: pointer; box-sizing: border-box; transition: border-color 0.2s;
//   &:focus { border-color: rgba(79,70,229,0.55); }
//   &:disabled { opacity: 0.5; cursor: not-allowed; }
// `;

const TextareaWrapper = styled.div`position: relative;`;

const StyledTextarea = styled.textarea`
  background: ${p => p.active ? '#1a1a1a' : '#f7f8fa'};
  border: 1px solid ${p => p.active ? '#2a2a2a' : '#e4e4e7'};
  border-radius: 9px; padding: 9px 13px 26px; font-size: 13.5px;
  color: ${p => p.active ? '#e0e0e0' : '#222'};
  outline: none; width: 100%; box-sizing: border-box;
  resize: vertical; min-height: 90px; max-height: 180px;
  transition: border-color 0.2s; font-family: inherit; line-height: 1.55;
  &:focus { border-color: rgba(79,70,229,0.55); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &::placeholder { color: ${p => p.active ? '#333' : '#c5c5c5'}; }
`;

const CharCount = styled.span`
  position: absolute; bottom: 7px; right: 11px;
  font-size: 10px; font-weight: 600; letter-spacing: 0.3px;
  color: ${p => p.nearLimit ? '#f87171' : p.active ? '#333' : '#d0d0d0'};
`;

const VisibilityRow = styled.div`display: flex; gap: 7px;`;

const VisChip = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 8px 10px; border-radius: 9px; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.18s; outline: none; flex: 1; justify-content: center;
  border: 1px solid ${p => !p.selected ? (p.active ? '#2a2a2a' : '#e4e4e7') : p.isPublic ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.5)'};
  background: ${p => !p.selected ? 'transparent' : p.isPublic ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)'};
  color: ${p => !p.selected ? (p.active ? '#777' : '#666') : p.isPublic ? '#4ade80' : '#f87171'};
  &:hover:not(:disabled) {
    border-color: ${p => p.isPublic ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.5)'};
    color: ${p => p.isPublic ? '#4ade80' : '#f87171'};
    background: ${p => p.isPublic ? 'rgba(34,197,94,0.07)' : 'rgba(239,68,68,0.07)'};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

/* Gallery */
const GalleryRow = styled.div`display: flex; gap: 8px; flex-wrap: wrap;`;

const GalleryThumb = styled.div`
  position: relative; width: 74px; height: 74px; border-radius: 8px; overflow: hidden;
  border: 1px solid rgba(129,140,248,0.25);
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const RemoveThumb = styled.button`
  position: absolute; top: 3px; right: 3px;
  width: 17px; height: 17px; border-radius: 50%;
  background: rgba(220,53,69,0.85); border: none;
  color: #fff; font-size: 8px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: #dc3545; }
`;

const GalleryAdd = styled.div`
  width: 74px; height: 74px; border-radius: 8px;
  border: 2px dashed ${p => p.active ? '#2a2a2a' : '#ddd'};
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 3px; cursor: pointer;
  color: ${p => p.active ? '#444' : '#bbb'}; font-size: 9px;
  position: relative; transition: all 0.2s;
  &:hover { border-color: rgba(79,70,229,0.5); color: #818cf8; }
  svg { font-size: 17px; }
`;

/* Files */
const ProductFileHint = styled.p`
  margin: 0; font-size: 11px;
  color: ${p => p.active ? '#bebdbd' : '#aaa'};
  padding: 7px 11px; border-radius: 7px;
  background: ${p => p.active ? 'rgba(129,140,248,0.05)' : 'rgba(79,70,229,0.03)'};
  border: 1px solid ${p => p.active ? 'rgba(129,140,248,0.1)' : 'rgba(79,70,229,0.08)'};
`;

const FileList = styled.div`display: flex; flex-direction: column; gap: 5px;`;

const FileItem = styled.div`
  display: flex; align-items: center; gap: 9px;
  padding: 8px 11px; border-radius: 9px;
  background: ${p => p.active ? '#181818' : '#f7f8fa'};
  border: 1px solid ${p => p.active ? '#242424' : '#e8e8e8'};
  transition: border-color 0.2s;
  &:hover { border-color: rgba(79,70,229,0.35); }
`;

const FileIconSpan = styled.span`font-size: 18px; flex-shrink: 0;`;
const FileName     = styled.span`flex: 1; font-size: 12px; font-weight: 500; color: ${p => p.active ? '#ccc' : '#333'}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
const FileSize     = styled.span`font-size: 10px; font-weight: 600; flex-shrink: 0; color: ${p => p.active ? '#bebdbd' : '#bbb'};`;

const RemoveFileBtn = styled.button`
  background: none; border: none; cursor: pointer; padding: 3px;
  border-radius: 5px; display: flex; align-items: center; justify-content: center;
  font-size: 10px; transition: all 0.2s; flex-shrink: 0;
  color: ${p => p.active ? '#444' : '#ccc'};
  &:hover { background: rgba(239,68,68,0.12); color: #f87171; }
`;

const CountBadge = styled.span`
  margin-left: 8px; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 20px;
  text-transform: none; letter-spacing: 0;
  background: ${p => p.active ? 'rgba(129,140,248,0.1)' : 'rgba(79,70,229,0.07)'};
  border: 1px solid ${p => p.active ? 'rgba(129,140,248,0.2)' : 'rgba(79,70,229,0.15)'};
  color: ${p => p.active ? '#818cf8' : '#4f46e5'};
`;

/* Drop zones */
const DropZone = styled.div`
  position: relative;
  border: 2px dashed ${p => p.dragOver ? 'rgba(79,70,229,0.75)' : p.active ? '#242424' : '#ddd'};
  border-radius: 12px; min-height: ${p => p.compact ? '88px' : '120px'};
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; overflow: hidden; transition: all 0.2s;
  background: ${p => p.dragOver ? 'rgba(79,70,229,0.04)' : p.active ? '#161616' : '#fafafa'};
  &:hover { border-color: rgba(79,70,229,0.45); background: ${p => p.active ? '#1a1a1a' : '#f5f5ff'}; }
`;

const DropContent  = styled.div`display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 16px; text-align: center;`;
const DropIcon     = styled.div`font-size: 26px; color: ${p => p.active ? '#777777' : '#ccc'}; transition: color 0.2s;`;
const DropText     = styled.p`margin: 0; font-size: 12px; color: ${p => p.active ? '#7a7a7a' : '#aaa'}; display: flex; align-items: center; gap: 5px; flex-wrap: wrap; justify-content: center;`;

const FormatBadgeRow = styled.div`display: flex; flex-wrap: wrap; gap: 5px; justify-content: center;`;
const FormatBadge    = styled.span`
  font-size: 9px; font-weight: 700; padding: 2px 7px; border-radius: 5px;
  background: ${p => p.active ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'};
  border: 1px solid ${p => p.active ? '#2a2a2a' : '#e4e4e7'};
  color: ${p => p.active ? '#b0b0b0' : '#aaa'}; letter-spacing: 0.4px;
`;

const HiddenInput    = styled.input`position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%;`;
const HiddenInputOff = styled.input`display: none;`;

/* Preview */
const PreviewWrapper = styled.div`
  position: relative; border-radius: 11px; overflow: hidden;
  border: 1.5px solid rgba(34,197,94,0.35);
`;

const PreviewImg = styled.img`width: 100%; max-height: 230px; object-fit: cover; display: block;`;

const PreviewActions = styled.div`position: absolute; bottom: 9px; right: 9px; display: flex; gap: 7px;`;

const PreviewBtn = styled.button`
  display: flex; align-items: center; gap: 5px;
  padding: 6px 12px; border-radius: 8px; font-size: 11px; font-weight: 600; cursor: pointer;
  backdrop-filter: blur(8px); transition: all 0.18s;
  border: 1px solid rgba(255,255,255,0.2);
  background: ${p => p.variant === 'change' ? 'rgba(79,70,229,0.82)' : 'rgba(0,0,0,0.6)'};
  color: #fff;
  &:hover { background: ${p => p.variant === 'change' ? '#4f46e5' : 'rgba(0,0,0,0.85)'}; transform: translateY(-1px); }
`;

/* Video */
const VideoPreview = styled.div`
  margin-top: 10px; border-radius: 11px; overflow: hidden;
  border: 1px solid ${p => p.active ? '#242424' : 'rgba(128,128,128,0.2)'};
`;

const VideoPlaceholder = styled.div`
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 10px; min-height: 200px; border-radius: 12px; margin-top: 10px;
  border: 2px dashed ${p => p.active ? '#1e1e1e' : '#e4e4e7'};
  color: ${p => p.active ? '#2a2a2a' : '#ccc'};
  font-size: 12px;
`;

/* Footer */
const FormFooter = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 28px;
  border-top: 1px solid ${p => p.active ? '#1a1a1a' : '#f0f0f0'};
  background: ${p => p.active ? 'rgba(255,255,255,0.012)' : 'rgba(0,0,0,0.01)'};
  border-radius: 0 0 18px 18px;
  @media(max-width: 700px) {
    padding: 12px 16px;
  }
`;

const CancelBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 9px 18px; border-radius: 9px; font-size: 13px; font-weight: 600; cursor: pointer;
  background: ${p => p.active ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
  border: 1px solid ${p => p.active ? '#2a2a2a' : '#e4e4e7'};
  color: ${p => p.active ? '#777' : '#666'}; transition: opacity 0.2s;
  &:hover:not(:disabled) { opacity: 0.7; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const SaveBtn = styled.button`
  display: flex; align-items: center; gap: 8px;
  padding: 9px 22px; border-radius: 9px; font-size: 13px; font-weight: 600; cursor: pointer;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  border: none; color: #fff;
  box-shadow: 0 4px 14px rgba(79,70,229,0.3); transition: all 0.2s;
  &:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(79,70,229,0.4); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const Spinner = styled.div`
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff;
  border-radius: 50%; animation: ${spin} 0.7s linear infinite;
`;

const LightboxOverlay = styled.div`
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,0.94);
  display: flex; align-items: center; justify-content: center;
  animation: ${fadeInScale} 0.2s ease; backdrop-filter: blur(8px); cursor: zoom-out;
`;

const LightboxImg = styled.img`
  max-width: 92vw; max-height: 90vh; object-fit: contain;
  border-radius: 10px; box-shadow: 0 24px 80px rgba(0,0,0,0.7);
  cursor: default; animation: ${fadeInScale} 0.2s ease;
`;

const LightboxClose = styled.button`
  position: fixed; top: 20px; right: 24px;
  width: 40px; height: 40px; border-radius: 10px;
  background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2);
  color: #fff; font-size: 16px; cursor: pointer;
  display: flex; align-items: center; justify-content: center; transition: all 0.2s;
  &:hover { background: rgba(220,53,69,0.6); border-color: #dc3545; }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0px);  opacity: 1;   }
  45%       { transform: translateY(-10px); opacity: 0.5; }
  70%       { transform: translateY(-5px);  opacity: 0.8; }
`;

const ArrowIndicator = styled.div`
  display: flex;
  align-items: center;
  animation: ${bounce} 1.5s ease-in-out infinite;
  filter: drop-shadow(0 0 5px rgba(124, 58, 237, 0.45));
  margin-bottom: 8px;
`;

const ArrowSvg = styled.svg`
  width: 22px;
  height: 52px;
  filter: drop-shadow(0 0 6px rgba(124, 58, 237, 0.5));
`;

const EmptyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
`;

const EmptyTexts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
`;


// Y el componente de mensaje que ya tenías
const ErrorMessage = styled.p`
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
  animation: ${slideIn} 0.2s ease;
`;

// const StockRow = styled.div`display: flex; gap: 8px; align-items: center;`;

const StockHint = styled.p`
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.active ? '#6a6a6a' : '#aaa'};
  transition: color 0.2s;
`;

export default CreateContents;